import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ApiError } from '@/api/client';
import { useAuth } from '@/app/auth';
import {
  Button,
  Callout,
  Field,
} from '@/components/ui';
import {
  inputClass,
} from '@/utils/format';
import { AuthShell } from '@/features/auth/AuthShell';

const MIN_PASSWORD_LENGTH = 10;

export function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    password: '',
    organization_name: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(false);

  function update(key: keyof typeof form, value: string) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setFieldErrors({});

    if (form.password.length < MIN_PASSWORD_LENGTH) {
      setFieldErrors({ password: `Use at least ${MIN_PASSWORD_LENGTH} characters.` });
      return;
    }

    setSubmitting(true);
    try {
      const active = await register({
        email: form.email,
        password: form.password,
        full_name: form.full_name,
        organization_name: form.organization_name || undefined,
      });
      if (!active) { setCreated(true); return; }
      navigate('/billing', { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught.message);
        setFieldErrors(caught.fieldErrors);
      } else {
        setError('Could not create your account. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (created) return <AuthShell title="Account created" subtitle="Your workspace is ready for identity verification." footer={<Link to="/login">Return to sign in</Link>}><p className="text-sm text-muted">Contact the platform administrator to confirm ownership of {form.email}. After activation, choose a paid plan or redeem a valid Starter coupon.</p></AuthShell>;
  return (
    <AuthShell
      title="Create your workspace"
      subtitle="Create an account and choose a paid plan."
      footer={
        <p className="text-sm text-ink-muted">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-brand hover:underline">
            Sign in
          </Link>
        </p>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {error ? <Callout tone="danger">{error}</Callout> : null}

        <Field label="Your name" htmlFor="full_name" error={fieldErrors.full_name} required>
          <input
            id="full_name"
            required
            autoComplete="name"
            value={form.full_name}
            onChange={(event) => update('full_name', event.target.value)}
            className={inputClass}
          />
        </Field>

        <Field label="Work email" htmlFor="email" error={fieldErrors.email} required>
          <input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={form.email}
            onChange={(event) => update('email', event.target.value)}
            className={inputClass}
            placeholder="you@company.com"
          />
        </Field>

        <Field
          label="Password"
          htmlFor="password"
          hint={`At least ${MIN_PASSWORD_LENGTH} characters. Length matters more than symbols.`}
          error={fieldErrors.password}
          required
        >
          <input
            id="password"
            type="password"
            required
            autoComplete="new-password"
            minLength={MIN_PASSWORD_LENGTH}
            value={form.password}
            onChange={(event) => update('password', event.target.value)}
            className={inputClass}
          />
        </Field>

        <Field
          label="Workspace name"
          htmlFor="organization_name"
          hint="Optional — your company or agency name."
          error={fieldErrors.organization_name}
        >
          <input
            id="organization_name"
            value={form.organization_name}
            onChange={(event) => update('organization_name', event.target.value)}
            className={inputClass}
            placeholder="Acme Ltd"
          />
        </Field>

        <Button type="submit" variant="primary" loading={submitting} className="w-full">
          Create workspace
        </Button>
      </form>
    </AuthShell>
  );
}
